import React, { useEffect } from "react";
import { legalConfig } from "../data/legalConfig";
import { videos } from "../data/videos";
import { courses } from "../data/courses";
import { faq } from "../data/faq";
import { reviews } from "../data/reviews";

const SITE = legalConfig.websiteUrl;

const inject = (id, data) => {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

export const StructuredData = () => {
  useEffect(() => {
    // ── Organization (ProfessionalService) — areaServed (zone de service, pas de local public) ──
    inject("ld-organization", {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${SITE}#organization`,
      name: legalConfig.brandName,
      alternateName: legalConfig.publisherFullName,
      url: SITE,
      logo: `${SITE}/favicon-512x512.png`,
      image: `${SITE}/og-image.jpg`,
      description:
        "Cours de danse particuliers et collectifs au Luxembourg et en France : valse, rock, rock acrobatique, danse de salon, danse de mariage et ouverture de bal.",
      telephone: legalConfig.contactPhoneE164,
      email: legalConfig.contactEmail,
      priceRange: "€€",
      currenciesAccepted: "EUR",
      areaServed: [
        { "@type": "Country", name: "Luxembourg" },
        { "@type": "AdministrativeArea", name: "Esch-sur-Alzette" },
        { "@type": "AdministrativeArea", name: "Luxembourg-ville" },
        { "@type": "Country", name: "France" },
      ],
      identifier: [
        { "@type": "PropertyValue", propertyID: "SIREN", value: legalConfig.sirenRaw },
        { "@type": "PropertyValue", propertyID: "SIRET", value: legalConfig.siretRaw },
        { "@type": "PropertyValue", propertyID: "APE", value: legalConfig.apeCode },
      ],
      founder: { "@id": `${SITE}#person` },
      sameAs: [legalConfig.social.instagram, legalConfig.social.facebook],
    });

    // ── Person ──
    inject("ld-person", {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": `${SITE}#person`,
      name: legalConfig.publisherName,
      alternateName: legalConfig.publisherFullName,
      jobTitle: "Professeur de danse",
      worksFor: { "@id": `${SITE}#organization` },
      image: `${SITE}/og-image.jpg`,
      url: SITE,
      sameAs: [legalConfig.social.instagram, legalConfig.social.facebook],
    });

    // ── WebSite ──
    inject("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE}#website`,
      url: SITE,
      name: legalConfig.brandName,
      description: "Site officiel de Louis-Melchior Danse — Cours de danse au Luxembourg et en France.",
      inLanguage: "fr-FR",
      publisher: { "@id": `${SITE}#organization` },
    });

    // ── Services (Course) — un node par discipline ──
    const courseNodes = courses.map((c) => ({
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": `${SITE}/#course-${c.id}`,
      name: `Cours de ${c.title.toLowerCase()} au Luxembourg & en France`,
      description: c.description,
      provider: { "@id": `${SITE}#organization` },
      inLanguage: "fr-FR",
      educationalLevel: c.level,
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: ["Onsite"],
        location: { "@type": "Place", name: legalConfig.serviceAreaLabel },
        instructor: { "@id": `${SITE}#person` },
        offers: {
          "@type": "Offer",
          price: legalConfig.pricing.hourlyNumber,
          priceCurrency: "EUR",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: legalConfig.pricing.hourlyNumber,
            priceCurrency: "EUR",
            unitText: "heure",
          },
          availability: "https://schema.org/InStock",
          url: `${SITE}/#contact`,
        },
      },
    }));
    inject("ld-courses", courseNodes);

    // ── FAQPage ──
    inject("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((q) => ({
        "@type": "Question",
        name: q.q,
        acceptedAnswer: { "@type": "Answer", text: q.a },
      })),
    });

    // ── VideoObject — uniquement vidéos publiées sur Instagram ──
    const videoNodes = videos
      .filter((v) => v.instagramUrl)
      .map((v) => ({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: `${v.title} — ${legalConfig.brandName}`,
        description: v.subtitle,
        thumbnailUrl: v.poster.startsWith("http") ? v.poster : `${SITE}${v.poster}`,
        contentUrl: v.instagramUrl,
        embedUrl: v.instagramUrl,
        uploadDate: "2026-05-01",
        publisher: { "@id": `${SITE}#organization` },
      }));
    if (videoNodes.length) inject("ld-videos", videoNodes);

    // ── Review summary (uniquement avis réellement publiés sur le site, source Superprof) ──
    const realReviews = reviews
      .filter((r) => r.source === "Superprof")
      .map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.name },
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
        reviewBody: r.text,
        publisher: { "@type": "Organization", name: r.source },
      }));
    if (realReviews.length) {
      inject("ld-reviews", {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "@id": `${SITE}#organization`,
        review: realReviews,
      });
    }
  }, []);

  return null;
};
